// Generic optimistic-update component for variant pickers.
//
// On any product page where this element is present, a radio / select click
// inside <variant-picker> resolves the new variant id from the current
// option selections (via the joined-options → id map emitted alongside it)
// and synchronously writes:
//   - price + compare-at price + Horizon's sale/regular visibility classes
//   - the featured image (slideshow.select)
//   - Add-to-Cart button availability (disabled attribute)
//
// Horizon's variant-picker still fires its section-fetch + morph in the
// background; this just hides the ~50-150ms gap. Peers that need to
// override our writes (e.g. bc-custom-length rewriting price as length ×
// per-meter × pieces) defer their own writes to a microtask, which runs
// after all synchronous `change` listeners regardless of registration
// order.
//
// It also PRE-WARMS the image the swap will need, so the gallery lands on a
// cached photo instead of starting a fetch — see the block comment above
// _initPrewarm for which images that is and why (it is not all of them, and
// never inside the quick-add modal).

(() => {
  if (customElements.get('bc-variant-optimistic')) return;

  class BcVariantOptimistic extends HTMLElement {
    constructor() {
      super();
      this._onChange = this._onChange.bind(this);
    }

    connectedCallback() {
      try {
        const json = this.querySelector('script[data-bc-variants]')?.textContent || '{}';
        const parsed = JSON.parse(json);
        this.prices = parsed.prices || {};
        this.comparePrices = parsed.comparePrices || {};
        this.available = parsed.available || {};
        this.mediaIndex = parsed.media || {};
        this.byOptions = parsed.byOptions || {};
      } catch (_e) {
        this.prices = {};
        this.comparePrices = {};
        this.available = {};
        this.mediaIndex = {};
        this.byOptions = {};
      }

      this.currency = this.dataset.currency || 'USD';
      this.locale = this.dataset.locale || 'en';
      this.formatter = window.bcMoney.formatter(this.locale, this.currency);

      // When the modal-rendered product details are morphed into the
      // quick-add dialog the section ancestor is gone, so scope to the
      // dialog instead — without this fallback both the page-level and
      // modal-level instances would listen on `document` and cross-talk
      // (e.g. a radio click in the modal would flip the page's price).
      this.scope =
        this.closest('quick-add-dialog') ||
        this.closest('.shopify-section') ||
        document;
      this.scope.addEventListener('change', this._onChange);

      // Quick-add renders this component inside the modal; pre-warming there
      // would download product photos for a variant list the shopper opened
      // for two seconds, on top of the page they are actually reading. The
      // modal keeps the instant price / availability writes and pays nothing.
      this._inQuickAdd = Boolean(this.closest('quick-add-dialog'));
      if (!this._inQuickAdd) this._initPrewarm();
    }

    // ---- variant image pre-warming -------------------------------------
    //
    // Horizon's media-gallery renders every slide eagerly into the DOM but
    // marks all non-current slides as `loading="lazy"`. slideshow.select()
    // (called from _writeImage) just moves the carousel pointer — it does not
    // force the new slide's image to start downloading, so the swap still
    // blocks on the lazy fetch. Flipping a slide's <img> to `loading="eager"`
    // mutates the same element the gallery will show, so the swap finds it
    // cached; fetchpriority="low" keeps it out of the way of LCP.
    //
    // What changed: this used to warm EVERY variant's slide on load — 15
    // full-size photos on a 15-colour webbing, most of them never looked at.
    // The policy is now "warm the likely next image, not the whole catalog":
    //
    //   - HOVER / FOCUS (the precise signal): pointing at a swatch resolves
    //     the variant that swatch would select and warms only its slide,
    //     hundreds of milliseconds before the click. Zero waste — the shopper
    //     told us what they are considering.
    //   - IDLE NEIGHBOURS (the fallback): touch and keyboard shoppers never
    //     hover, so at idle we warm the few slides adjacent to the current
    //     one, which is how people step through a swatch row. Capped, nearest
    //     first, and skipped entirely under Save-Data / 2G, where spending a
    //     megabyte on a guess is the wrong trade.
    _initPrewarm() {
      this._warmed = new Set();
      // The gallery is replaced/morphed on every variant change (morph.js
      // resets `loading` to the server's `lazy`), so a position warmed on the
      // OLD gallery says nothing about the new one — forget them all.
      this._boundForgetWarmed = () => {
        this._warmed.clear();
        // The morph reuses the swatch inputs, so the last hover hint must not
        // short-circuit a re-hover on the now re-lazied gallery either.
        this._lastHinted = null;
      };
      document.addEventListener('variant:update', this._boundForgetWarmed);
      this._boundPrewarmHint = this._onPrewarmHint.bind(this);
      // pointerover/focusin (not pointerenter) so one delegated listener on
      // the scope covers every swatch, including ones morphed in later.
      this.scope.addEventListener('pointerover', this._boundPrewarmHint);
      this.scope.addEventListener('focusin', this._boundPrewarmHint);

      const run = () => this._warmIdleNeighbours();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, { once: true });
      } else {
        run();
      }
    }

    /** Slides adjacent to the selected variant's, at idle, capped. */
    _warmIdleNeighbours() {
      const conn = navigator.connection;
      if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return;

      const current = this.mediaIndex?.[this._resolveVariantId()];
      const positions = [];
      for (const variantId in this.mediaIndex) {
        const pos = this.mediaIndex[variantId];
        if (Number.isInteger(pos) && pos >= 0 && !positions.includes(pos)) positions.push(pos);
      }
      if (!positions.length) return;
      if (Number.isInteger(current)) {
        positions.sort((a, b) => Math.abs(a - current) - Math.abs(b - current));
      }

      const IDLE_WARM_CAP = 4;
      const warm = () => {
        for (const pos of positions.slice(0, IDLE_WARM_CAP)) this._warmPosition(pos);
      };
      if (window.requestIdleCallback) requestIdleCallback(warm, { timeout: 3000 });
      else setTimeout(warm, 1200);
    }

    /**
     * Hover / focus over a swatch: work out which variant it would select
     * (current selections with this one option value substituted) and warm
     * that variant's slide.
     */
    _onPrewarmHint(event) {
      const target = event.target;
      if (!target?.closest) return;
      // One ancestor walk on the hot path (this fires for every element the
      // pointer enters); everything below only runs for actual swatches.
      const el = target.closest('label, input[type="radio"]');
      if (!el) return;
      const input = el.tagName === 'INPUT' ? el : el.control;
      if (!input || input.type !== 'radio') return;
      if (input === this._lastHinted) return;
      this._lastHinted = input;
      if (!input.closest('variant-picker')) return;

      const containers = [...this.scope.querySelectorAll('.variant-option')];
      const idx = containers.findIndex((c) => c.contains(input));
      if (idx < 0) return;
      const values = window.bcVariantResolve?.readOptionValues(this.scope);
      if (!values || values.length !== containers.length) return;
      values[idx] = input.value;

      const pos = this.mediaIndex?.[this.byOptions?.[values.join('|')]];
      if (Number.isInteger(pos) && pos >= 0) this._warmPosition(pos);
    }

    /** Flip one slide's lazy imgs to a low-priority eager fetch, once. */
    _warmPosition(pos) {
      if (this._warmed.has(pos)) return;
      // Only remember positions we actually reached: a gallery that isn't in
      // the DOM yet must not be recorded as warmed.
      let found = false;
      this.scope.querySelectorAll('media-gallery').forEach((gallery) => {
        const slide = gallery.querySelectorAll('.product-media[data-media-id]')[pos];
        if (!slide) return;
        found = true;
        slide.querySelectorAll('img.product-media__image[loading="lazy"]').forEach((img) => {
          // Lower priority before flipping `loading`: Horizon's slide imgs
          // ship with fetchpriority="high", so setting eager first can
          // briefly initiate the fetch at high priority before the property
          // update lands.
          img.fetchPriority = 'low';
          img.loading = 'eager';
        });
      });
      if (found) this._warmed.add(pos);
    }

    disconnectedCallback() {
      this.scope?.removeEventListener('change', this._onChange);
      if (this._boundPrewarmHint) {
        this.scope?.removeEventListener('pointerover', this._boundPrewarmHint);
        this.scope?.removeEventListener('focusin', this._boundPrewarmHint);
      }
      if (this._boundForgetWarmed) {
        document.removeEventListener('variant:update', this._boundForgetWarmed);
        this._boundForgetWarmed = null;
      }
    }

    _onChange(event) {
      const t = event.target;
      if (!t || !t.matches) return;
      if (!t.matches('input[type="radio"]') && !t.matches('select')) return;
      // Only react to changes inside a real variant-picker — keeps us out of
      // unrelated forms in the same section (quantity selectors, custom-length
      // inputs that stopPropagation but might leak in edge cases).
      if (!t.closest('variant-picker')) return;

      const id = this._resolveVariantId();
      if (!id) return;

      this._writePrice(id);
      this._writeImage(id);
      this._writeAvailability(id);
    }

    _resolveVariantId() {
      // Delegate to the shared resolver (assets/bc-variant-resolve.js). The
      // `?.` guards the impossible-in-practice case where the helper script
      // hasn't loaded yet (this only runs on a `change` event, long after all
      // defer scripts execute).
      return window.bcVariantResolve?.resolveVariantId(this.scope, this.byOptions) || null;
    }

    _writePrice(variantId) {
      const cents = this.prices[variantId];
      if (!Number.isFinite(cents)) return;
      const formatted = `${this.formatter.format(cents / 100)} ${this.currency}`;
      const compareCents = this.comparePrices[variantId];
      const compareFormatted = Number.isFinite(compareCents)
        ? `${this.formatter.format(compareCents / 100)} ${this.currency}`
        : null;
      const containers = this.scope.querySelectorAll(
        'product-price, .sticky-add-to-cart__price'
      );
      for (const container of containers) {
        // Horizon renders both `.price__regular > .price` and
        // `.price__sale > .price-item--sale.price` and toggles visibility
        // via .price__hidden. Update every .price slot, then flip the
        // visibility classes so a regular → sale (or vice versa) variant
        // switch doesn't leave the wrong block showing until the morph
        // reconciles.
        container.querySelectorAll('.price').forEach((el) => {
          el.textContent = formatted;
        });
        const onSale = Boolean(compareFormatted);
        container.querySelectorAll('.price__regular').forEach((el) => {
          el.classList.toggle('price__hidden', onSale);
        });
        container.querySelectorAll('.price__sale').forEach((el) => {
          el.classList.toggle('price__hidden', !onSale);
        });
        container.querySelectorAll('.compare-at-price').forEach((el) => {
          el.textContent = compareFormatted || '';
        });
      }
    }

    _writeImage(variantId) {
      const idx = this.mediaIndex?.[variantId];
      if (!Number.isInteger(idx) || idx < 0) return;
      // `.select(index)` is the same call path the zoom-dialog uses.
      this.scope.querySelectorAll('media-gallery').forEach((gallery) => {
        gallery.refs?.slideshow?.select?.(idx, undefined, { animate: false });
      });
    }

    _writeAvailability(variantId) {
      const isAvailable = this.available?.[variantId] === true;
      // Horizon's morph on variant:update will reconcile the inner text /
      // icon shortly; we just need the button to flip clickable instantly.
      this.scope.querySelectorAll('[ref="addToCartButton"]').forEach((btn) => {
        if (isAvailable) btn.removeAttribute('disabled');
        else btn.setAttribute('disabled', '');
      });
    }
  }

  customElements.define('bc-variant-optimistic', BcVariantOptimistic);
})();
